import { useState, useRef, useEffect } from "react";

const ContactSearchDropdown = ({
  contacts,
  value,
  onChange,
  placeholder = "-- Select a Contact --",
  itemsPerPage = 10,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [displayedContacts, setDisplayedContacts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter contacts based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredContacts(contacts);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = contacts.filter((contact) =>
        (contact.Name || "").toLowerCase().includes(term) ||
        (contact.Surname || "").toLowerCase().includes(term) ||
        (contact.email || "").toLowerCase().includes(term)
      );
      setFilteredContacts(filtered);
    }
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, contacts]);

  // Paginate filtered contacts
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setDisplayedContacts(filteredContacts.slice(startIndex, endIndex));
  }, [filteredContacts, currentPage, itemsPerPage]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const selectedContact = contacts.find((c) => c.id === value);
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);

  console.log("ContactSearchDropdown contacts:", contacts);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Display Selected Value */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-2 border border-gray-300 rounded text-left bg-white hover:bg-gray-50 transition flex justify-between items-center gap-3"
      >
        <span className="min-w-0 flex-1 truncate">
          {selectedContact
            ? `${selectedContact.Name} ${selectedContact.Surname} <${selectedContact.email}>`
            : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="relative mt-1 w-full bg-white border border-gray-300 rounded shadow-lg z-50 max-h-96 flex flex-col">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200 sticky top-0 bg-white">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name, surname, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#90c740]"
            />
            <div className="text-xs text-gray-500 mt-2">
              {filteredContacts.length} results
              {filteredContacts.length > itemsPerPage &&
                ` (showing ${(currentPage - 1) * itemsPerPage + 1}-${Math.min(
                  currentPage * itemsPerPage,
                  filteredContacts.length
                )})`}
            </div>
          </div>

          {/* Contact List */}
          <div className="overflow-y-auto flex-1">
            {displayedContacts.length > 0 ? (
              displayedContacts.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => {
                    onChange({
                      target: { value: contact.id },
                    });
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  className={`w-full p-3 text-left hover:bg-blue-50 border-b border-gray-100 transition ${
                    value === contact.id ? "bg-blue-100 font-semibold" : ""
                  }`}
                >
                  <div className="font-medium text-gray-900">
                    {contact.Name} {contact.Surname}
                  </div>
                  <div className="text-sm text-gray-600">{contact.email}</div>
                </button>
              ))
            ) : (
              <div className="p-3 text-center text-gray-500">No contacts found</div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50 sticky bottom-0 flex justify-between items-center">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactSearchDropdown;
